// Built-in workflow templates for projects that work with OpenSpec (plan:
// docs/plans/2026-10-04-openspec.md §4, §5; CLI behavior checked in §10).
//
//  - `openspec-init`: a developer run sets up `openspec/` in a repository
//    that has none (`openspec init --tools none`, so the CLI writes no files
//    for other agent tools), fills `openspec/config.yaml`, then a human
//    reviews the pull request.
//  - `feature-openspec`: Planner writes a change (proposal, specs, design,
//    tasks), a human approves it, Developer implements the tasks and
//    archives the change in the same branch, then a human reviews the pull
//    request. Planner commits only under `openspec/changes/`: the run's
//    changes are committed to the task branch and pushed (draft pull
//    request), and the Developer run continues on that branch.
//
// The general method (what openspec/ is, the commands) is in the run's
// context for projects with `specMode: 'openspec'` (api compose-spec.ts);
// the step instructions below only say what this step does. They stay under
// the 8000-character limit of `WorkflowStateSchema.instructions`.

import type { WorkflowDefinition, WorkflowState, WorkflowTemplate } from '../types/workflow.js'

const INIT_ROLE = 'Developer'

export const OPENSPEC_INIT_INSTRUCTIONS = [
  'Goal: set up OpenSpec in this repository so later changes can be specified there. You change only the `openspec/` folder.',
  '',
  '1. If `openspec/` already exists, do not run `init`. Read `openspec/config.yaml`, check it, and say in your summary what you found.',
  '2. Otherwise run `openspec init --tools none --no-animation`. Keep `--tools none`: without it the CLI writes files for other agent tools into the repository. It creates `openspec/config.yaml`, `openspec/specs/` and `openspec/changes/archive/`.',
  '3. Fill in `openspec/config.yaml` (follow the comments in the file):',
  '   - `context`: only what an agent cannot find out by reading the code (constraints, the language specs are written in, compatibility promises). Read the README and the knowledge base (`kb_read project/overview.md`) first. Do not copy general documentation into it.',
  '   - `rules`: add rules per artifact only where the project has clear ones (for example how tasks are sized). Leave the examples commented out if there is nothing to say.',
  '4. Do not write specs for existing behavior in this task; that is a separate decision.',
  '5. Run `openspec validate --all` and `openspec list --json`; both must run without errors.',
  '',
  'Rules:',
  '- No secrets, tokens or private hostnames in any file.',
  '- Do not change anything outside `openspec/`.',
  '',
  'Finish with a summary: what existed, what you created, what you put in `config.yaml` and why.',
].join('\n')

export const OPENSPEC_INIT_REVIEW_INSTRUCTIONS =
  'Check openspec/config.yaml in the pull request: is the context accurate and free of secrets, are the rules ones the team wants, and did the agent leave everything outside openspec/ alone? Reject to send the step back with your notes.'

export const OPENSPEC_PROPOSE_INSTRUCTIONS = [
  'Goal: turn this task into an OpenSpec change that a developer can build from without asking you anything. You write the change; you do not change application code or tests.',
  '',
  '1. Read what exists: the task (`get_task`), `openspec/config.yaml`, the specs your task touches under `openspec/specs/`, and `openspec list --json` for changes already in progress. If a change in progress covers the same ground, say so in your summary instead of creating a second one.',
  '2. Create one change: `openspec new change <name>` with a short kebab-case name. Use `openspec status --change <name> --json` and, for each artifact in turn (proposal, specs, design, tasks), `openspec instructions <artifact> --change <name> --json`; follow what it says. Skip an artifact only when its instructions say it is optional for this kind of change, and say why in your summary.',
  '3. Specs are deltas: describe added, modified or removed requirements with scenarios, not the whole system. Each requirement must be testable.',
  '   If the change intentionally changes no behavior the specs describe (a refactor, tooling, docs), skip the deltas: add the line `skip_specs: true` to the change’s `.openspec.yaml` and say why in `proposal.md`.',
  '4. `tasks.md` is the build list for the next step: small, ordered, each task something one developer run can finish and verify. Include the tests.',
  '5. Run `openspec validate <name>` and fix everything it reports. Goblic runs the same check when you finish: a change that does not validate blocks the run and nothing is pushed.',
  '',
  'Rules:',
  '- Write only under `openspec/changes/<name>/`. Do not edit `openspec/specs/` (it changes when the change is archived) and do not archive.',
  '- Do not guess at unclear requirements: list them under open questions in `proposal.md` and in your summary, or ask with `ask_human` if you cannot go on without an answer.',
  '- Keep it as small as the task allows. A change that needs more than about 15 tasks is probably two changes; propose the split.',
  '',
  'Finish with a summary: the change name, the capabilities it adds or changes, the number of tasks, and the open questions.',
].join('\n')

export const OPENSPEC_PROPOSAL_REVIEW_INSTRUCTIONS =
  'Read the change under openspec/changes/ on the task branch (proposal, specs, design, tasks). Is this what you want built? Are the requirements testable, the tasks small and ordered, the open questions answered? Approve to start the implementation. Reject to send it back to the planner with your notes: the notes are the only input it gets.'

export const OPENSPEC_IMPLEMENT_INSTRUCTIONS = [
  'Goal: build what the approved OpenSpec change describes, and archive the change when it is done. The change is on this branch under `openspec/changes/`; find its name with `openspec list --json` (the one in progress for this task).',
  '',
  '1. Read the change: `proposal.md`, the spec deltas, `design.md` and `tasks.md`. `openspec instructions apply --change <name> --json` shows the progress.',
  '2. Do the tasks in order. After each one, tick it off in `tasks.md` (`- [x]`). Write the tests the tasks ask for and keep the whole test suite green.',
  '3. If a task turns out wrong or impossible, or the change is missing something, stop widening the scope on your own: do what you can, leave the task unticked, and say in your summary what is missing so a human can send it back to the planner.',
  '4. When every task is ticked and the tests pass, archive in this branch: `openspec archive <name> --yes` (add `--skip-specs` when the change has `skip_specs: true`). This merges the spec deltas into `openspec/specs/`. Fill in the `## Purpose` of every new spec it created: archiving leaves a `TBD` text there.',
  '5. Run `openspec validate --all` and fix what it reports. Goblic runs the same check when you finish: a change or spec you touched that does not validate, or an archived change with open tasks, blocks the run and nothing is pushed.',
  '',
  'Rules:',
  '- Implement only what the tasks list. Behavior the change does not describe does not get built.',
  '- Do not edit the spec deltas to make them match your code. If the code cannot follow the spec, that is a question for a human.',
  '- Do not archive while tasks are open.',
  '',
  'Finish with a summary: tasks done and not done, whether the change is archived, the result of `openspec validate --all`, and anything a human should look at.',
].join('\n')

export const OPENSPEC_PR_REVIEW_INSTRUCTIONS =
  'Review the pull request: does the code do what the change’s specs say, are the tests there, are all tasks ticked, is the change archived (openspec/changes/archive/) and openspec/specs/ updated, and does every new spec have a real Purpose instead of TBD? Reject to send it back to the developer with your notes.'

function agent(
  name: string,
  agentRole: string,
  instructions: string,
  next: { onSuccess: string; onFailure: string; maxRounds: number; freshContext?: boolean },
): WorkflowState {
  return {
    name,
    kind: 'agent',
    agentRole,
    instructions,
    requires: [],
    blocks: [],
    freshContext: next.freshContext ?? false,
    onSuccess: next.onSuccess,
    onFailure: next.onFailure,
    maxRounds: next.maxRounds,
  }
}

function human(name: string, instructions: string, onApprove: string, onReject: string): WorkflowState {
  return { name, kind: 'human', instructions, requires: [], blocks: [], onApprove, onReject }
}

function terminal(name: string, outcome: 'success' | 'failure'): WorkflowState {
  return { name, kind: 'terminal', requires: [], blocks: [], outcome }
}

function definition(key: string, name: string, description: string, states: WorkflowState[]): WorkflowDefinition {
  return { key, name, description, initialState: states[0].name, states, templateKey: key }
}

export const openspecInitTemplate: WorkflowTemplate = {
  key: 'openspec-init',
  name: 'OpenSpec init',
  description:
    'Developer sets up OpenSpec in a repository that has none (openspec init, config.yaml), then a human reviews the pull request. Run once per project that switches to OpenSpec.',
  requiredRoles: [INIT_ROLE],
  definition: definition('openspec-init', 'OpenSpec init', 'Set up openspec/ → review.', [
    agent('Init', INIT_ROLE, OPENSPEC_INIT_INSTRUCTIONS, { onSuccess: 'Review', onFailure: 'Blocked', maxRounds: 3 }),
    human('Review', OPENSPEC_INIT_REVIEW_INSTRUCTIONS, 'Done', 'Init'),
    terminal('Done', 'success'),
    terminal('Blocked', 'failure'),
  ]),
}

export const featureOpenspecTemplate: WorkflowTemplate = {
  key: 'feature-openspec',
  name: 'Feature (OpenSpec)',
  description:
    'Planner writes an OpenSpec change (proposal, specs, design, tasks), a human approves it, developer builds the tasks and archives the change in the same branch, then a human reviews the pull request. For projects with the OpenSpec specification method.',
  requiredRoles: ['Planner', 'Developer'],
  definition: definition('feature-openspec', 'Feature (OpenSpec)', 'Propose → review proposal → implement → review PR.', [
    agent('Propose', 'Planner', OPENSPEC_PROPOSE_INSTRUCTIONS, { onSuccess: 'Review proposal', onFailure: 'Blocked', maxRounds: 3 }),
    human('Review proposal', OPENSPEC_PROPOSAL_REVIEW_INSTRUCTIONS, 'Implement', 'Propose'),
    // Fresh context: the change on disk is the handover; a clean start keeps the
    // build from leaning on half-ideas from the design phase.
    agent('Implement', 'Developer', OPENSPEC_IMPLEMENT_INSTRUCTIONS, {
      onSuccess: 'Review PR',
      onFailure: 'Blocked',
      maxRounds: 3,
      freshContext: true,
    }),
    human('Review PR', OPENSPEC_PR_REVIEW_INSTRUCTIONS, 'Done', 'Implement'),
    terminal('Done', 'success'),
    terminal('Blocked', 'failure'),
  ]),
}

/** Both OpenSpec templates, for appending to `WORKFLOW_TEMPLATES`. */
export const OPENSPEC_WORKFLOW_TEMPLATES: readonly WorkflowTemplate[] = [openspecInitTemplate, featureOpenspecTemplate]
